import React, { useEffect, useState } from "react";
import { ref, getDownloadURL, uploadBytes } from "firebase/storage";
import { storage } from "../../../pages/auth/common/firebaseConfig";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";

const EventImageUploadInput = ({
  label,
  setFieldValue,
  error,
  name,
  defaultValue = "",
}) => {
  const [imageUrl, setImageUrl] = useState(defaultValue);
  const [uploadError, setUploadError] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    setImageUrl(defaultValue);
    setUploadError("");
  }, [defaultValue]);

  const handleChange = async (event) => {
    const file = event.target.files[0];

    if (file) {
      setIsUploading(true);
      setUploadProgress(0);
      setUploadError("");

      try {
        // Validate file size
        if (file.size > 2 * 1024 * 1024) {
          throw new Error("File size exceeds 2MB limit");
        }

        // Validate file format
        const allowedFormats = ["jpg", "jpeg", "png"];
        const fileFormat = file.name.split(".").pop().toLowerCase();
        if (!allowedFormats.includes(fileFormat)) {
          throw new Error(
            "Invalid file format. Allowed formats: jpg, jpeg, png"
          );
        }

        // Show upload started notification
        toast.info("Uploading image...", { autoClose: 2000 });

        // Simulate progress (Firebase doesn't provide progress callback for uploadBytes)
        const progressInterval = setInterval(() => {
          setUploadProgress((prev) => {
            if (prev >= 90) {
              clearInterval(progressInterval);
              return 90;
            }
            return prev + 10;
          });
        }, 200);

        const storageRef = ref(storage, `images/${file.name}`);
        await uploadBytes(storageRef, file);

        clearInterval(progressInterval);
        setUploadProgress(100);

        const url = await getDownloadURL(storageRef);
        setImageUrl(url);
        setFieldValue(name, url);
        setUploadError("");

        // Show success notification
        toast.success("Image uploaded successfully!", { autoClose: 3000 });
      } catch (error) {
        console.error(error);
        setUploadError(error.message);
        setImageUrl("");
        setFieldValue(name, "");

        // Show error notification
        toast.error(`Upload failed: ${error.message}`, { autoClose: 4000 });
      } finally {
        setIsUploading(false);
        setTimeout(() => setUploadProgress(0), 1000);
      }
    } else {
      setImageUrl("");
      setFieldValue(name, "");
      setUploadError("");
    }
  };

  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>
      <div className="relative">
        <input
          type="file"
          accept=".jpg, .jpeg, .png"
          onChange={handleChange}
          disabled={isUploading}
          className={`mb-2 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 ${
            isUploading ? "opacity-50 cursor-not-allowed" : ""
          }`}
        />

        {/* Upload Progress Indicator */}
        {isUploading && (
          <div className="mt-2">
            <div className="flex items-center space-x-2">
              <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <span className="text-xs text-gray-600 min-w-[3rem] text-right">
                {uploadProgress}%
              </span>
            </div>
            <div className="flex items-center mt-1 text-sm text-blue-600">
              <Icon icon="heroicons-outline:arrow-up-tray" className="mr-1" />
              <span>Uploading...</span>
            </div>
          </div>
        )}

        {/* Success Indicator */}
        {imageUrl && !isUploading && (
          <div className="flex items-center mt-2 text-sm text-green-600">
            <Icon icon="heroicons-outline:check-circle" className="mr-1" />
            <span>Image ready</span>
          </div>
        )}
      </div>

      {/* Image Preview */}
      {imageUrl && (
        <div className="mt-3 relative inline-block">
          <img
            src={imageUrl}
            alt="Uploaded Preview"
            className="h-32 w-32 object-cover rounded-md border-2 border-gray-200"
          />
          {!isUploading && (
            <div className="absolute top-1 right-1 bg-green-500 rounded-full p-1">
              <Icon
                icon="heroicons-outline:check"
                className="text-white text-xs"
              />
            </div>
          )}
        </div>
      )}

      {/* Error Messages */}
      {(uploadError || error) && (
        <div className="mt-2 flex items-center text-sm text-red-500">
          <Icon icon="heroicons-outline:exclamation-circle" className="mr-1" />
          <span>{uploadError || error}</span>
        </div>
      )}
    </div>
  );
};

export default EventImageUploadInput;
